import { useTranslation } from "react-i18next";
import { UserType } from "../models/UserType";
import { FreelancerOnboardingStatus } from "../models/enums/FreelancerOnboardingStatus";
import { IndustryType } from "../models/IndustryType";
import { CompanySize } from "../models/CompanySize";
import { CompanyType } from "../models/companyType";
import {
	ProjectDomain,
	MissionType,
	ProjectType,
	ProjectLocation,
	ProjectStatus,
	DurationType
} from "../models/Project";

/**
 * Custom hook for translating enum values
 * @returns Object with translation functions for different enums
 */
export const useEnumTranslation = () => {
	const { t } = useTranslation();

	/**
	 * Translates a UserType enum value
	 * @param type UserType enum value
	 * @returns Translated string for the user type
	 */
	const translateUserType = (type: UserType | string): string => {
		return t(`enums.userType.${type}`);
	};

	/**
	 * Translates a FreelancerOnboardingStatus enum value
	 * @param status FreelancerOnboardingStatus enum value
	 * @returns Translated string for the freelancer status
	 */
	const translateFreelancerOnboardingStatus = (
		status: FreelancerOnboardingStatus | string
	): string => {
		return t(`enums.FreelancerOnboardingStatus.${status}`);
	};

	/**
	 * Translates an IndustryType enum value
	 * @param industry IndustryType enum value
	 * @returns Translated string for the industry type
	 */
	const translateIndustryType = (industry: IndustryType | string): string => {
		if (!industry) return "";
		return t(`enums.industryType.${industry}`);
	};

	/**
	 * Translates a CompanySize enum value
	 * @param size CompanySize enum value
	 * @returns Translated string for the company size
	 */
	const translateCompanySize = (size: CompanySize | string): string => {
		if (!size) return "";
		return t(`enums.companySize.${size}`);
	};

	/**
	 * Translates a CompanyType enum value
	 * @param type CompanyType enum value
	 * @returns Translated string for the company type
	 */
	const translateCompanyType = (type: CompanyType | string): string => {
		if (!type) return "";
		return t(`enums.companyType.${type}`);
	};

	/**
	 * Translates a ProjectDomain enum value
	 * @param domain ProjectDomain enum value
	 * @returns Translated string for the project domain
	 */
	const translateProjectDomain = (domain: ProjectDomain | string): string => {
		if (!domain) return "";
		return t(`enums.projectDomain.${domain}`);
	};

	/**
	 * Translates a MissionType enum value
	 * @param missionType MissionType enum value
	 * @returns Translated string for the mission type
	 */
	const translateMissionType = (missionType: MissionType | string): string => {
		if (!missionType) return "";
		return t(`enums.missionType.${missionType}`);
	};

	/**
	 * Translates a ProjectType enum value
	 * @param type ProjectType enum value
	 * @returns Translated string for the project type
	 */
	const translateProjectType = (type: ProjectType | string): string => {
		if (!type) return "";
		return t(`enums.projectType.${type}`);
	};

	/**
	 * Translates a ProjectLocation enum value
	 * @param location ProjectLocation enum value
	 * @returns Translated string for the project location
	 */
	const translateProjectLocation = (location: ProjectLocation | string): string => {
		if (!location) return "";
		return t(`enums.projectLocation.${location}`);
	};

	/**
	 * Translates a ProjectStatus enum value
	 * @param status ProjectStatus enum value
	 * @returns Translated string for the project status
	 */
	const translateProjectStatus = (status: ProjectStatus | string): string => {
		if (!status) return "";
		return t(`enums.projectStatus.${status}`);
	};

	/**
	 * Translates a DurationType enum value
	 * @param type DurationType enum value
	 * @returns Translated string for the duration type
	 */
	const translateDurationType = (type: DurationType | string): string => {
		if (!type) return "";
		return t(`enums.durationType.${type}`);
	};

	return {
		translateUserType,
		translateFreelancerOnboardingStatus,
		translateIndustryType,
		translateCompanySize,
		translateCompanyType,
		translateProjectDomain,
		translateMissionType,
		translateProjectType,
		translateProjectLocation,
		translateProjectStatus,
		translateDurationType
	};
};
